import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../api/client';
import { API_URL } from '../config';
import RangeToolbar from './RangeToolbar';
import AttendanceZoneMap from './AttendanceZoneMap';
import './WorkReportTable.css';

// Attendance (Work Performance): clock-in / clock-out marks from the warehouse scanner next
// to the time in Daily Reports, with flags for the manager, plus the phones and scanner
// screens allowed to mark. Employees use the Clock app (server/public/clock) on their own
// phone; a one-time code from here connects a phone or a screen. Docs: server/docs/attendance.md

const FLAGS = {
  no_scan: ['No scan', 'Time in Daily Reports was entered by hand, there are no scans that day'],
  no_out: ['No clock out', 'The day ended without a clock-out scan'],
  no_geo: ['Location not confirmed', 'The phone did not confirm it was at the warehouse'],
  differs: ['Edited by hand', 'Time or break in Daily Reports differs from the scans'],
  close: ['Scanned together', 'Another employee scanned within 20 seconds, one person may have scanned for both'],
};
const errMsg = (e, fallback) => (e.response && e.response.data && e.response.data.error) || fallback;
const fmtDay = iso => new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
const fmt12 = t => {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};
const fmtWhen = iso => (iso ? new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const fullName = e => [e.employeeName, e.employeeSurname].filter(Boolean).join(' ');
const STATUS = { bound: ['Connected', 'wp-badge--on'], pending: ['Waiting for code', 'wp-badge--wait'], expired: ['Code expired', 'wp-badge--off'] };

const AttendanceTab = ({ search = '', employeeId = '', employees = [] }) => {
  const [view, setView] = useState('marks');   // marks | devices
  const [range, setRange] = useState(null);
  const [days, setDays] = useState(null);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [devices, setDevices] = useState(null);
  const [clockUrl, setClockUrl] = useState(`${API_URL}/clock/`); // the Clock app address; the server sends its own domain when it has one
  const [err, setErr] = useState(null);
  const [issued, setIssued] = useState(null);   // { title, code, expiresMin, url } — the one-time code just created
  const [confirm, setConfirm] = useState(null); // { title, text, label, run }
  const [newScreen, setNewScreen] = useState(null); // label being typed
  const [help, setHelp] = useState(false);
  // Zones: meta from GET attendance-devices (default site, radius limits, whether any zone is active)
  const [meta, setMeta] = useState({ site: null, zoneRadiusM: 300, zoneRadiusRange: [50, 5000], zonesActive: false });
  const [zoneEdit, setZoneEdit] = useState(null); // { id, label, point: {lat,lng} | null, radiusM, saving, error }

  const loadDays = useCallback(async () => {
    if (!range) return;
    try { const r = await api.get('/api/work-reports/attendance', { params: range }); setDays(r.data.days || []); setErr(null); }
    catch (e) { setErr(errMsg(e, 'Failed to load the marks')); setDays([]); }
  }, [range]);
  const loadDevices = useCallback(async () => {
    try {
      const r = await api.get('/api/work-reports/attendance-devices');
      setDevices(r.data.devices || []); setErr(null);
      if (r.data.clockUrl) setClockUrl(r.data.clockUrl.replace(/[/]*$/, '/'));
      setMeta({ site: r.data.site || null, zoneRadiusM: r.data.zoneRadiusM || 300, zoneRadiusRange: r.data.zoneRadiusRange || [50, 5000], zonesActive: !!r.data.zonesActive });
    }
    catch (e) { setErr(errMsg(e, 'Failed to load phones and screens')); setDevices([]); }
  }, []);
  useEffect(() => { if (view === 'marks') loadDays(); }, [view, loadDays]);
  useEffect(() => { if (view === 'devices') loadDevices(); }, [view, loadDevices]);

  const rows = useMemo(() => (days || []).filter(d =>
    (!employeeId || String(d.employeeID) === String(employeeId)) &&
    (!search || String(d.name || '').toLowerCase().includes(search)) &&
    (!flaggedOnly || d.flags.length)
  ), [days, employeeId, search, flaggedOnly]);

  const byDay = useMemo(() => {
    const m = new Map();
    for (const r of rows) { if (!m.has(r.workday)) m.set(r.workday, []); m.get(r.workday).push(r); }
    return [...m.entries()].map(([workday, list]) => ({ workday, rows: list }));
  }, [rows]);

  const phoneOf = useMemo(() => new Map((devices || []).filter(d => d.kind === 'phone').map(d => [d.employeeID, d])), [devices]);
  const screens = useMemo(() => (devices || []).filter(d => d.kind === 'kiosk'), [devices]);
  const people = useMemo(() => employees.filter(e =>
    (!employeeId || String(e.employeeID) === String(employeeId)) &&
    (!search || fullName(e).toLowerCase().includes(search))
  ), [employees, employeeId, search]);

  const issue = async (body, title, url) => {
    try {
      const r = await api.post('/api/work-reports/attendance-devices', body);
      setIssued({ title, url, code: r.data.code, expiresMin: r.data.expiresMin });
      loadDevices();
    } catch (e) { setErr(errMsg(e, 'Failed to create the code')); }
  };
  const remove = async id => {
    try { await api.delete(`/api/work-reports/attendance-devices/${id}`); loadDevices(); }
    catch (e) { setErr(errMsg(e, 'Failed to disconnect')); }
  };
  const openZone = sc => setZoneEdit({ id: sc.id, label: sc.label, point: sc.zone ? { lat: sc.zone.lat, lng: sc.zone.lng } : null, radiusM: sc.zone ? sc.zone.radiusM : meta.zoneRadiusM, saving: false, error: null });
  const saveZone = async clear => {
    const z = zoneEdit;
    const r = Number(z.radiusM);
    if (!clear && (!z.point || !Number.isFinite(r) || r < meta.zoneRadiusRange[0] || r > meta.zoneRadiusRange[1])) {
      return setZoneEdit({ ...z, error: `Put the pin on the map and set a radius of ${meta.zoneRadiusRange[0]} to ${meta.zoneRadiusRange[1]} m` });
    }
    setZoneEdit({ ...z, saving: true, error: null });
    try {
      await api.put(`/api/work-reports/attendance-devices/${z.id}`, { zone: clear ? null : { lat: z.point.lat, lng: z.point.lng, radiusM: r } });
      setZoneEdit(null); loadDevices();
    } catch (e) { setZoneEdit({ ...z, saving: false, error: errMsg(e, 'Failed to save the zone') }); }
  };
  const fmtZone = z => (z ? `${z.radiusM} m around ${z.lat.toFixed(4)}, ${z.lng.toFixed(4)}${z.accuracyM ? ` (found within ${z.accuracyM} m)` : ''}` : null);
  const connectPhone = e => {
    const run = () => issue({ employeeID: e.employeeID }, `Phone for ${fullName(e)}`, clockUrl);
    if (phoneOf.get(e.employeeID)) setConfirm({ title: `Replace the phone of ${fullName(e)}?`, text: 'The current phone stops working right away. One employee has one phone.', label: 'Replace', run });
    else run();
  };

  return (
    <div className="wp-att">
      <div className="wp-att-switch">
        <button className={`pp-tgl${view === 'marks' ? ' on' : ''}`} onClick={() => setView('marks')}>Marks</button>
        <button className={`pp-tgl${view === 'devices' ? ' on' : ''}`} onClick={() => setView('devices')}>Phones and screens</button>
        <button className="wp-att-help-btn" title="How it works" aria-label="How it works" onClick={() => setHelp(true)}><i className="fas fa-question" /></button>
      </div>
      {view === 'marks' && (
        <RangeToolbar presets={[1, 2, 4]} initialWeeks={1} onChange={setRange}>
          <button className={`pp-tgl wp-att-flagged${flaggedOnly ? ' on' : ''}`} onClick={() => setFlaggedOnly(v => !v)}>Flagged only</button>
        </RangeToolbar>
      )}
      {err && <div className="pp-modal-err">{err}</div>}

      {view === 'marks' && (
        <div className="att-days">
          {days && !rows.length && (
            <div className="wrt-empty"><i className="fas fa-qrcode" /><span>{days.length ? 'No days match the filters' : 'No marks in this period'}</span></div>
          )}
          {byDay.map(day => {
            const flagged = day.rows.filter(r => r.flags.length).length;
            return (
              <section key={day.workday} className="wrt-week">
                <div className="wrt-week__header att-day__head">
                  <i className="fas fa-calendar-day wrt-cal-icon" />
                  <span className="wrt-week__label">{fmtDay(day.workday)}</span>
                  <span className="wrt-week__stats">
                    <span className="wrt-badge wrt-badge--blue">{day.rows.length} {day.rows.length === 1 ? 'employee' : 'employees'}</span>
                    {flagged ? <span className="wrt-badge att-badge--warn">{flagged} flagged</span> : <span className="wrt-badge wrt-badge--green">No flags</span>}
                  </span>
                </div>
                <table className="att-table">
                  <thead><tr><th>Employee</th><th>Scans</th><th>Daily Reports</th><th>Flags</th></tr></thead>
                  <tbody>{day.rows.map(d => <DayRow key={d.employeeID} d={d} />)}</tbody>
                </table>
              </section>
            );
          })}
        </div>
      )}

      {view === 'devices' && (
        <>
          <div className="wp-att-head">
            <span className="wp-att-title">Scanner screens</span>
            <button className="pp-btn" onClick={() => setNewScreen('Warehouse PC')}><i className="fas fa-plus" /> Scanner screen</button>
            {/* The scanner page address stays at hand after a screen is added (the hint below the table is only shown while there are none) */}
            <a className="pp-btn wp-att-link" href={`${clockUrl}kiosk.html`} target="_blank" rel="noopener noreferrer"><i className="fas fa-external-link-alt" /> Open scanner page</a>
          </div>
          <div className="wp-table-wrap">
            <table className="wp-table wp-att-table">
              <thead><tr><th>Screen</th><th>Status</th><th>Zone</th><th>Connected</th><th>Last scan</th><th /></tr></thead>
              <tbody>
                {devices && !screens.length && <tr><td colSpan={6} className="wp-empty">No scanner screen yet. Add one and open {clockUrl}kiosk.html on the warehouse computer.</td></tr>}
                {screens.map(s => (
                  <tr key={s.id}>
                    <td className="wp-cell-name"><span className="wp-name">{s.label}</span></td>
                    <td data-label="Status"><span className={`wp-badge ${STATUS[s.status][1]}`}>{STATUS[s.status][0]}</span></td>
                    <td data-label="Zone">{s.zone ? fmtZone(s.zone) : <span className="att-none">No zone</span>}</td>
                    <td data-label="Connected">{fmtWhen(s.bound_at)}</td>
                    <td data-label="Last scan">{fmtWhen(s.last_seen)}</td>
                    <td className="wp-cell-edit"><button className="pp-btn" onClick={() => openZone(s)}><i className="fas fa-map-marker-alt" /> Map</button><button className="pp-btn" onClick={() => setConfirm({ title: `Disconnect ${s.label}?`, text: 'Scans from this screen stop working until it is activated again.', label: 'Disconnect', run: () => remove(s.id) })}>Disconnect</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {devices && meta.site && (
            <p className="att-zone-note">
              {meta.zonesActive
                ? 'Phones get their codes inside the zones of the connected screens.'
                : `No screen has a zone yet, so phones are checked against the default warehouse point: ${meta.site.radiusM} m around ${meta.site.lat}, ${meta.site.lng}. A screen gets its zone when it is activated, or from the Map button.`}
            </p>
          )}
          <div className="wp-att-head"><span className="wp-att-title">Phones</span></div>
          <div className="wp-table-wrap">
            <table className="wp-table wp-att-table">
              <thead><tr><th>Employee</th><th>Phone</th><th>Connected</th><th>Last used</th><th /></tr></thead>
              <tbody>
                {!people.length && <tr><td colSpan={5} className="wp-empty">No employees match</td></tr>}
                {people.map(e => {
                  const p = phoneOf.get(e.employeeID);
                  return (
                    <tr key={e.employeeID}>
                      <td className="wp-cell-name"><span className="wp-name">{fullName(e)}</span></td>
                      <td data-label="Phone"><span className={`wp-badge ${p ? STATUS[p.status][1] : 'wp-badge--off'}`}>{p ? STATUS[p.status][0] : 'Not connected'}</span></td>
                      <td data-label="Connected">{p ? fmtWhen(p.bound_at) : ''}</td>
                      <td data-label="Last used">{p ? fmtWhen(p.last_seen) : ''}</td>
                      <td className="wp-cell-edit">
                        <button className="pp-btn" onClick={() => connectPhone(e)}>{p ? 'Replace phone' : 'Connect phone'}</button>
                        {p && <button className="pp-btn" onClick={() => setConfirm({ title: `Disconnect the phone of ${fullName(e)}?`, text: 'The employee will not be able to clock in until a phone is connected again.', label: 'Disconnect', run: () => remove(p.id) })}>Disconnect</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {zoneEdit && (
        <div className="pp-modal-overlay" onClick={() => !zoneEdit.saving && setZoneEdit(null)}>
          <div className="pp-modal wp-att-zone" onClick={e => e.stopPropagation()}>
            <div className="pp-modal-title">Zone of {zoneEdit.label}</div>
            <p className="wp-att-note">Phones get their codes inside this circle. Drag the pin or click the map to move it. Other screens are grey.</p>
            <AttendanceZoneMap screens={screens} selectedId={zoneEdit.id} site={meta.site} point={zoneEdit.point} radiusM={Number(zoneEdit.radiusM) || meta.zoneRadiusM}
              onMove={p => setZoneEdit(z => ({ ...z, point: p, error: null }))} />
            <div className="att-zone-bar">
              {!zoneEdit.point && <button className="pp-btn" onClick={() => setZoneEdit(z => ({ ...z, point: { lat: meta.site.lat, lng: meta.site.lng } }))}>Put the pin at the warehouse</button>}
              {zoneEdit.point && <span className="pp-muted">Pin at {zoneEdit.point.lat.toFixed(5)}, {zoneEdit.point.lng.toFixed(5)}</span>}
              <label className="att-zone-radius">Radius <input type="number" min={meta.zoneRadiusRange[0]} max={meta.zoneRadiusRange[1]} step={10} value={zoneEdit.radiusM} onChange={e => setZoneEdit(z => ({ ...z, radiusM: e.target.value }))} /> m</label>
            </div>
            {zoneEdit.error && <div className="pp-modal-err">{zoneEdit.error}</div>}
            <div className="pp-modal-actions">
              {zoneEdit.point && <button className="pp-btn att-zone-remove" disabled={zoneEdit.saving} onClick={() => saveZone(true)}>Remove zone</button>}
              <button className="pp-btn" disabled={zoneEdit.saving} onClick={() => setZoneEdit(null)}>Cancel</button>
              <button className="pp-btn pp-btn-primary" disabled={zoneEdit.saving || !zoneEdit.point} onClick={() => saveZone(false)}>{zoneEdit.saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
      {help && (
        <div className="pp-modal-overlay" onClick={() => setHelp(false)}>
          <div className="pp-modal wp-att-help" onClick={e => e.stopPropagation()}>
            <div className="pp-modal-title">How attendance works</div>
            <h4>1. Scanner screen, once</h4>
            <ol>
              <li>Press <b>Scanner screen</b> above the table and name it. You get a 6-digit code.</li>
              <li>On the warehouse computer open <b>{clockUrl}kiosk.html</b> and enter the code.</li>
              <li>The page shows a map: check that the pin is on the warehouse, drag it if it is off, press <b>Activate here</b>. Phones get their codes within {meta.zoneRadiusM} m of that pin. The <b>Map</b> button next to the screen moves it later.</li>
              <li>Keep that page open and active. The scanner types into it like a keyboard.</li>
            </ol>
            <div className="wp-att-help__shots">
              <Shot src="screen-add" w="sm" text="The button above the table" />
              <Shot src="screen-code" w="sm" text="The code you get" />
              <Shot src="kiosk-activate" w="sm" text="The warehouse computer" />
              <Shot src="kiosk-zone" w="md" text="The map before activation" />
            </div>
            <h4>2. Phones, once per employee</h4>
            <ol>
              <li>Press <b>Connect phone</b> next to the employee. You get a 6-digit code.</li>
              <li>On the employee's phone open <b>{clockUrl}</b> and enter the code. An iPhone asks to add the app to the Home Screen first.</li>
              <li>A code works once and expires in 15 minutes. One phone per employee: <b>Replace phone</b> disconnects the old one.</li>
            </ol>
            <div className="wp-att-help__shots">
              <Shot src="phone-code-dialog" w="md" text="The code you get" />
              <Shot src="phone-connect" w="sm" text="The employee's phone" />
            </div>
            <h4>3. Every day</h4>
            <ul>
              <li>The employee opens the Clock Organo app. It shows a QR code only at the warehouse.</li>
              <li>The employee holds the phone up to the scanner. The screen shows the name and the time.</li>
              <li>The time goes to Daily Reports: clock in fills the start if it is empty, clock out sets the end. With more than two scans a day the break is counted from them. Manual entry still works.</li>
            </ul>
            <div className="wp-att-help__shots">
              <Shot src="phone-code" w="sm" text="The Clock app at the warehouse" />
              <Shot src="kiosk-ok" w="md" text="The scanner screen after a scan" />
            </div>
            <h4>4. Marks</h4>
            <ul>
              <li>Days with scans next to the Daily Reports entry. A value that differs from the scans is underlined.</li>
              {Object.values(FLAGS).map(([name, text]) => <li key={name}><b>{name}</b>: {text.toLowerCase()}</li>)}
            </ul>
            <div className="pp-modal-actions"><button className="pp-btn pp-btn-primary" onClick={() => setHelp(false)}>Got it</button></div>
          </div>
        </div>
      )}
      {issued && (
        <div className="pp-modal-overlay" onClick={() => setIssued(null)}>
          <div className="pp-modal wp-att-modal" onClick={e => e.stopPropagation()}>
            <div className="pp-modal-title">{issued.title}</div>
            <div className="wp-att-code">{issued.code}</div>
            <p className="wp-att-note">Open <b>{issued.url}</b> on the device and enter this code. It works once and expires in {issued.expiresMin} minutes.</p>
            <div className="pp-modal-actions"><button className="pp-btn pp-btn-primary" onClick={() => setIssued(null)}>Done</button></div>
          </div>
        </div>
      )}
      {confirm && (
        <div className="pp-modal-overlay" onClick={() => setConfirm(null)}>
          <div className="pp-modal wp-att-modal" onClick={e => e.stopPropagation()}>
            <div className="pp-modal-title">{confirm.title}</div>
            <p className="wp-att-note">{confirm.text}</p>
            <div className="pp-modal-actions">
              <button className="pp-btn" onClick={() => setConfirm(null)}>Cancel</button>
              <button className="pp-btn pp-btn-primary" onClick={() => { const run = confirm.run; setConfirm(null); run(); }}>{confirm.label}</button>
            </div>
          </div>
        </div>
      )}
      {newScreen != null && (
        <div className="pp-modal-overlay" onClick={() => setNewScreen(null)}>
          <form className="pp-modal wp-att-modal" onClick={e => e.stopPropagation()}
            onSubmit={e => { e.preventDefault(); const label = newScreen.trim() || 'Warehouse PC'; setNewScreen(null); issue({ kiosk: true, label }, `Scanner screen: ${label}`, `${clockUrl}kiosk.html`); }}>
            <div className="pp-modal-title">New scanner screen</div>
            <label>Name<input type="text" maxLength={80} value={newScreen} onChange={e => setNewScreen(e.target.value)} autoFocus /></label>
            <div className="pp-modal-actions">
              <button type="button" className="pp-btn" onClick={() => setNewScreen(null)}>Cancel</button>
              <button type="submit" className="pp-btn pp-btn-primary">Create code</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

// Illustration in the help popup: client/public/help/attendance/<src>.png, w = sm | md (width class)
const Shot = ({ src, w, text }) => (
  <figure className={w}><img src={`/help/attendance/${src}.png`} alt="" loading="lazy" /><figcaption>{text}</figcaption></figure>
);

// One employee's day: the scans as a timeline, the Daily Reports entry with the values that differ
// from the scans marked, and the flags. "open" = no end yet (still on shift or never clocked out).
const DayRow = ({ d }) => {
  const last = d.events[d.events.length - 1];
  const hasReport = d.reportStart || d.reportEnd || d.reportBreak > 0;
  const startDiff = !!d.firstIn && d.reportStart !== d.firstIn;
  const endDiff = last ? (last.kind === 'out' ? d.reportEnd !== d.lastOut : d.reportEnd != null) : false;
  const breakDiff = d.scanBreak > 0 && d.reportBreak !== d.scanBreak;
  const diffTitle = 'Differs from the scans';
  return (
    <tr>
      <td className="att-emp" data-label="Employee"><span className="wp-avatar">{(d.name || '?').charAt(0).toUpperCase()}</span>{d.name}</td>
      <td data-label="Scans">
        {d.events.length ? (
          <ol className="att-scans">
            {d.events.map((ev, i) => (
              <li key={i} className={`att-scan att-scan--${ev.kind}${ev.geo === 'ok' ? '' : ' att-scan--nogeo'}`} title={ev.geo === 'ok' ? undefined : FLAGS.no_geo[1]}>
                <span className="att-scan__dot" />{ev.kind === 'in' ? 'In' : 'Out'} <b>{fmt12(ev.time)}</b>
              </li>
            ))}
            {d.scanBreak > 0 && <li className="att-scans__break">{d.scanBreak} min break</li>}
          </ol>
        ) : <span className="att-none">No scans</span>}
      </td>
      <td className="att-report-cell" data-label="Daily Reports">
        {hasReport ? (
          <span className="att-report">
            <b className={startDiff ? 'att-diff' : ''} title={startDiff ? diffTitle : undefined}>{fmt12(d.reportStart) || 'no start'}</b>
            <span className="att-report__sep">to</span>
            <b className={endDiff ? 'att-diff' : ''} title={endDiff ? diffTitle : undefined}>{fmt12(d.reportEnd) || 'open'}</b>
            {d.reportBreak > 0 && <span className={`att-report__break${breakDiff ? ' att-diff' : ''}`} title={breakDiff ? diffTitle : undefined}>{d.reportBreak} min break</span>}
          </span>
        ) : <span className="att-none">No entry</span>}
      </td>
      <td className="att-flags" data-label="Flags">
        {d.flags.map(f => <span key={f} className="att-flag" title={FLAGS[f][1]}>{FLAGS[f][0]}</span>)}
      </td>
    </tr>
  );
};

export default AttendanceTab;
